import { FC, useState } from 'react';
import {
  useSubmitMediaFeedbackMutation,
  useGetCurrentUserQuery,
  type ObjectiveMedia,
} from '@teachur/http-client';

import { Button } from '@new/common/ui/Button/Button';
import { MediaInfo } from './MediaInfo';
import { FeedbackItem } from './FeedbackItem';
import Modal from '@new/common/ui/Modal/Modal';

interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  media: ObjectiveMedia;
  objectiveName?: string;
  objectiveId?: number;
  rating: number;
  courseName?: string;
}

export const ReportModal: FC<ReportModalProps> = ({
  open,
  onClose,
  media,
  objectiveName,
  objectiveId,
  rating,
  courseName,
}) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const [formData, setFormData] = useState({
    mediaNotWorkingComment: '',
    mediaIncorrectComment: '',
    mediaOffensiveComment: '',
    additionalComment: '',
  });

  const submitReportMutation = useSubmitMediaFeedbackMutation({
    onSuccess: () => {
      setFormData({
        mediaNotWorkingComment: '',
        mediaIncorrectComment: '',
        mediaOffensiveComment: '',
        additionalComment: '',
      });
      onClose();
    },
  });

  const handleReportSubmit = () => {
    if (!objectiveId) {
      return;
    }

    submitReportMutation.mutate({
      objectiveId,
      mediaId: media.id,
      objectiveName: objectiveName || '',
      courseName: courseName || '',
      mediaDescription: media.description || '',
      mediaNotWorkingComment: formData.mediaNotWorkingComment,
      mediaIncorrectComment: formData.mediaIncorrectComment,
      mediaOffensiveComment: formData.mediaOffensiveComment,
      additionalComment: formData.additionalComment,
      rating,
      userId: currentUser?.id,
      userName: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : undefined,
      userEmail: currentUser?.email,
      mediaUrl: media.url,
      fileUrl: media.file?.url,
    });
  };

  const handleCancel = () => {
    setFormData({
      mediaNotWorkingComment: '',
      mediaIncorrectComment: '',
      mediaOffensiveComment: '',
      additionalComment: '',
    });
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleCancel}
      title="Report an issue with this media"
      className="h-[90vh] w-150"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto pr-2">
          <div className="flex flex-col gap-2 lg:gap-4">
            <MediaInfo objectiveName={objectiveName} media={media} />

            <div className="flex flex-col gap-5">
              <FeedbackItem
                label="Is the media missing or not working?"
                value={formData.mediaNotWorkingComment}
                onChange={(value) => setFormData({ ...formData, mediaNotWorkingComment: value })}
              />

              <FeedbackItem
                label="Is the media incorrect?"
                value={formData.mediaIncorrectComment}
                onChange={(value) => setFormData({ ...formData, mediaIncorrectComment: value })}
              />

              <FeedbackItem
                label="Is the media offensive?"
                value={formData.mediaOffensiveComment}
                onChange={(value) => setFormData({ ...formData, mediaOffensiveComment: value })}
              />

              <FeedbackItem
                label="Any additional details?"
                value={formData.additionalComment}
                onChange={(value) => setFormData({ ...formData, additionalComment: value })}
              />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 gap-3 border-t border-(--neutral-black-NB100) pt-4">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={submitReportMutation.isPending}
            className="flex-1 rounded-md!"
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleReportSubmit}
            disabled={submitReportMutation.isPending}
            className="flex-1"
          >
            {submitReportMutation.isPending ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
